import {
  Badge,
  createStyles,
  getStylesRef,
  Group,
  Navbar as MantineNavBar,
  rem,
  Text,
  UnstyledButton
} from "@mantine/core";
import {spotlight} from "@mantine/spotlight";
import {
  IconHelpCircle,
  IconLayoutNavbar,
  IconLogout,
  IconUserCircle,
  IconSearch,
  IconTools,
  } from "@tabler/icons-react";
import {NavLink} from "react-router-dom";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {
  changeRegionAndResetData,
  getCurrentRegion,
  RegionEntity,
  useGetPersonQuery
} from "@ots-frontend/ots-data";
import {loadBaseData, logout} from "../../store/auth/actions";
import {useCallback, useMemo} from "react";
import {useAppThunkDispatch} from "../../store/hooks/useAppThunkDispatch";
import {skipToken} from "@reduxjs/toolkit/query";
import {HasAccess, useAccessibleRegions, useAuth} from "@ots-frontend/ots-ui";
import {NavLinkItem, useNavLinks} from "../../hooks/useNavLinks";
import {useNavLayout} from "../../hooks/useNavLayout";
import {useAuth0} from "@auth0/auth0-react";
import {OtsRegionSwitcher} from "@ots-frontend/ots-ui-tw";

type MainLink = NavLinkItem

const useStyles = createStyles((theme) => ({
  navbar: {
    paddingTop: 0
  },

  title: {
    textTransform: "uppercase",
    letterSpacing: rem(-0.25)
  },

  section: {
    marginLeft: `calc(${theme.spacing.md} * -1)`,
    marginRight: `calc(${theme.spacing.md} * -1)`,
    marginBottom: theme.spacing.md,

    "&:not(:last-of-type)": {
      borderBottom: `${rem(1)} solid ${
        theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[3]
      }`
    }
  },

  search: {
    height: rem(34),
    paddingLeft: theme.spacing.sm,
    paddingRight: rem(5),
    borderRadius: theme.radius.md,
    color: theme.colorScheme === "dark" ? theme.colors.dark[2] : theme.colors.gray[5],
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.white,
    border: `${rem(1)} solid ${
      theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[3]
    }`,

    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.fn.rgba(theme.colors.dark[5], 0.85)
          : theme.fn.rgba(theme.colors.gray[0], 0.35)
    }
  },

  searchCode: {
    fontWeight: 700,
    fontSize: rem(10),
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.colors.gray[0],
    border: `${rem(1)} solid ${
      theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.colors.gray[2]
    }`
  },

  mainLinks: {
    paddingLeft: `calc(${theme.spacing.md} - ${theme.spacing.xs})`,
    paddingRight: `calc(${theme.spacing.md} - ${theme.spacing.xs})`,
    paddingBottom: theme.spacing.md
  },

  mainLink: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    fontSize: theme.fontSizes.xs,
    padding: `${rem(8)} ${theme.spacing.xs}`,
    borderRadius: theme.radius.sm,
    fontWeight: 500,
    color: theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.colors.gray[7],

    "&:hover": {
      backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
      color: theme.colorScheme === "dark" ? theme.white : theme.black
    }
  },

  mainLinkActive: {
    "&, &:hover": {
      backgroundColor: theme.fn.variant({variant: "light", color: theme.primaryColor}).background,
      color: theme.fn.variant({variant: "light", color: theme.primaryColor}).color,
      fontWeight: 600,
      [`& .${getStylesRef("mainLinkIcon")}`]: {
        color: theme.fn.variant({variant: "light", color: theme.primaryColor}).color
      }
    }
  },

  mainLinkInner: {
    display: "flex",
    alignItems: "center",
    flex: 1
  },

  mainLinkIcon: {
    ref: getStylesRef("mainLinkIcon"),
    marginRight: theme.spacing.sm,
    color: theme.colorScheme === "dark" ? theme.colors.dark[2] : theme.colors.gray[6]
  },

  mainLinkBadge: {
    padding: 0,
    width: rem(20),
    height: rem(20),
    pointerEvents: "none"
  },

  link: {
    ...theme.fn.focusStyles(),
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    fontSize: theme.fontSizes.sm,
    color: theme.colorScheme === "dark" ? theme.colors.dark[1] : theme.colors.gray[7],
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.radius.sm,
    fontWeight: 500,

    "&:hover": {
      backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
      color: theme.colorScheme === "dark" ? theme.white : theme.black,

      [`& .${getStylesRef("icon")}`]: {
        color: theme.colorScheme === "dark" ? theme.white : theme.black
      }
    }
  },

  linkIcon: {
    ref: getStylesRef("icon"),
    color: theme.colorScheme === "dark" ? theme.colors.dark[2] : theme.colors.gray[6],
    marginRight: theme.spacing.sm
  },

  linkActive: {
    "&, &:hover": {
      backgroundColor: theme.fn.variant({variant: "light", color: theme.primaryColor}).background,
      color: theme.fn.variant({variant: "light", color: theme.primaryColor}).color,
      [`& .${getStylesRef("icon")}`]: {
        color: theme.fn.variant({variant: "light", color: theme.primaryColor}).color
      }
    }
  },

  footer: {
    marginLeft: `calc(${theme.spacing.md} * -1)`,
    marginRight: `calc(${theme.spacing.md} * -1)`
  }
}));

export const NavBar = () => {
  const {classes, cx} = useStyles();

  const dispatch = useAppThunkDispatch();
  const {isAuthenticated, logout: auth0Logout} = useAuth0();

  const handleRegionChange = useCallback((region: RegionEntity | undefined) => {
    if (region) {
      dispatch(changeRegionAndResetData(region.id, loadBaseData));
    }
  }, [dispatch]);

  const {regions, canSwitchRegion} = useAccessibleRegions();

  const currentRegion = useAppSelector<RegionEntity | undefined>(getCurrentRegion);

  const {user} = useAuth();

  const onLogoutClick = useCallback(() => {
    dispatch(logout());
    if (isAuthenticated) {
      try {
        auth0Logout({logoutParams: {returnTo: window.location.origin}})
      } catch (error) {
        console.error(error)
      }
    }
  }, [dispatch, isAuthenticated, auth0Logout]);

  const links: MainLink[] = useNavLinks();
  const [, setNavLayout] = useNavLayout();

  const mainLinks = useMemo(() => {
    return links.map((link) => (
      <HasAccess accessOnEmpty={true} permissions={link.permissions} key={link.label}>
        <NavLink key={link.label} to={link.path}>
          {({isActive}) => (
          <UnstyledButton key={link.label} className={cx(classes.mainLink, isActive && classes.mainLinkActive)} aria-current={isActive ? "page" : undefined}>
            <div className={classes.mainLinkInner}>
              <link.icon size={20} className={classes.mainLinkIcon} stroke={1.5}/>
              <span>{link.label}</span>
            </div>
            {link.notifications && (
              <Badge size="sm" variant="filled" className={classes.mainLinkBadge}>
                {link.notifications}
              </Badge>
            )}
          </UnstyledButton>
          )}
        </NavLink>
      </HasAccess>
    ));
  }, [links, classes]);

  return (
    <MantineNavBar width={{sm: 225}} p="md" className={classes.navbar}>
      {user &&
        <Text weight={500} size="sm" className={classes.title} color="dimmed" mb="xs" truncate>
          {user.emailAddress ?? `${user.firstName} ${user.lastName}`}
        </Text>
      }
      {currentRegion &&
        <MantineNavBar.Section className={classes.section}>
          <OtsRegionSwitcher
            currentRegion={currentRegion}
            regions={regions}
            canSwitchRegion={canSwitchRegion}
            onRegionChange={handleRegionChange}
            variant="sidebar"/>
        </MantineNavBar.Section>
      }
      <UnstyledButton className={classes.search} mb="sm" onClick={() => spotlight.open()}>
        <Group spacing="xs">
          <IconSearch size="0.8rem" stroke={1.5}/>
          <Text size="sm" color="dimmed" pr={80}>
            Search
          </Text>
        </Group>
      </UnstyledButton>
      <MantineNavBar.Section className={classes.section} grow>
        <div className={classes.mainLinks}>{mainLinks}</div>
      </MantineNavBar.Section>
      <MantineNavBar.Section className={classes.footer}>
        <NavLink to="/profile">
          <UnstyledButton className={classes.link}>
            <IconUserCircle className={classes.linkIcon} stroke={1.5}/>
            <span>Profile</span>
          </UnstyledButton>
        </NavLink>
        <NavLink to="/help">
          <UnstyledButton className={classes.link}>
            <IconHelpCircle className={classes.linkIcon} stroke={1.5}/>
            <span>Help &amp; Support</span>
          </UnstyledButton>
        </NavLink>
        <UnstyledButton className={classes.link} onClick={() => setNavLayout("top")}>
          <IconLayoutNavbar className={classes.linkIcon} stroke={1.5}/>
          <span>Use top navigation</span>
        </UnstyledButton>
        <NavLink to={""}>
          <UnstyledButton className={classes.link} onClick={onLogoutClick}>
            <IconLogout className={classes.linkIcon} stroke={1.5}/>
            <span>Logout</span>
          </UnstyledButton>
        </NavLink>
      </MantineNavBar.Section>
    </MantineNavBar>
  );
};
